import dayjs from "dayjs";
import i18n from "i18next";
import { isEmpty } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useFormatCurrency } from "@krea/customer-features/hooks";
import { settings } from "@krea/customer-features/settings";
import LayoutOneColumn from "@krea/customer-features/shared-components/app/one-column";
import BankIDFlow from "@krea/customer-features/shared-components/bankID/BankIDFlow";
import FIBankIDFlow from "@krea/customer-features/shared-components/bankID-fi/FIBankIDFlow";
import Button from "@krea/customer-features/shared-components/button";
import ErrorBlock from "@krea/customer-features/shared-components/error-block";
import { REASONS } from "@krea/customer-features/shared-components/loan-application-form/fields/reason";
import Preloader from "@krea/customer-features/shared-components/preloader";
import { Text } from "@krea/customer-features/shared-components/text";
import TextActionLink from "@krea/customer-features/shared-components/text/TextActionLink";
import { Toast } from "@krea/customer-features/shared-components/toast/Toast";
import { useBankIDStore } from "@krea/customer-features/store/bankID/hooks";
import { useLoanApplicationFormStore } from "@krea/customer-features/store/loan-application-form/hooks";
import { useLogout } from "@krea/customer-features/store/user/hooks/mutations/useLogout";
import { fetchUserQuery } from "@krea/customer-features/store/user/hooks/queries/useGetUserData";
import {
  APPLICATION_NAME,
  termsOfServiceLink,
} from "@krea/customer-features/utils";
import { CountryCode } from "@krea/shared/types/common";

import { useGetIdentifyCustomerData } from "store/hooks/queries";

import { NotFound } from "../not-found";

export const IdentifyCustomer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const identifyToken = searchParams.get("identify");

  const [isToastOpen, setIsToastOpen] = useState(true);
  const [isFinnishBankIDOpen, setIsFinnishBankIDOpen] = useState(false);
  const [formattedCurrencyValue] = useFormatCurrency({
    currency: settings.currency,
  });

  const resetBankIDStore = useBankIDStore(
    ({ resetBankIDStore }) => resetBankIDStore,
  );
  const setRemoteIdentification = useLoanApplicationFormStore(
    ({ setRemoteIdentification }) => setRemoteIdentification,
  );

  const {
    data: identifyResponse,
    isLoading,
    isError,
    refetch,
  } = useGetIdentifyCustomerData(identifyToken!);
  const { mutate: logout } = useLogout();

  const isSweden = settings.countryCode === CountryCode.SE;
  const title = isSweden
    ? t("remoteIdentification.SMSIdentifyKrea")
    : t("remoteIdentification.SMSIdentifyKredita");
  const { data: identifyData, id, type } = identifyResponse ?? {};
  const {
    reason,
    requestedAmount,
    customerCompany,
    expiresAt,
    isCustomerApplicationFlowAllowed = true,
    status,
    initiatingUserMobilePhoneNumber,
  } = identifyData ?? {};

  const organisationName = customerCompany?.name;
  const identificationContext = {
    id: id ?? "",
    type: type ?? "UNKNOWN",
  };

  const getReason = () => {
    return REASONS.find(({ value }) => value === reason)?.name;
  };

  const onSuccess = useCallback(async () => {
    if (!isCustomerApplicationFlowAllowed) {
      navigate("/identify/success");
      logout(undefined);
    } else {
      await fetchUserQuery();
      navigate("/");
    }

    resetBankIDStore();
  }, [navigate, logout, resetBankIDStore, isCustomerApplicationFlowAllowed]);

  const contentData = [
    {
      label: t("applicationForm.commons.requestedAmountPreLabel"),
      value: formattedCurrencyValue(requestedAmount ?? 0),
    },
    { label: t("remoteIdentification.reasonLabelSMS"), value: getReason() },
  ];

  const { errorCode } = useLoanApplicationFormStore((state) => ({
    clearFormErrors: state.clearFormErrors,
    errorCode: state.formErrors.errorCode,
  }));

  useEffect(() => {
    const linkHasExpired = dayjs().isAfter(dayjs(expiresAt));

    if (linkHasExpired) {
      navigate("/direct/loan-application-form");
    }
  }, [navigate, expiresAt]);

  useEffect(() => {
    if (!isEmpty(identifyResponse)) {
      setRemoteIdentification({
        isCustomerApplicationFlowAllowed:
          !!identifyResponse.data?.isCustomerApplicationFlowAllowed,
      });
    }
  }, [setRemoteIdentification, identifyResponse]);

  if (isError || !identifyToken || (!isLoading && isEmpty(identifyResponse))) {
    return <NotFound />;
  } else if (status === "failed") {
    if (errorCode === 1007) {
      return (
        <ErrorBlock
          title={t("remoteIdentification.fraudClientErrorPage.title")}
          description={
            <Trans
              t={t}
              values={{ mobilePhoneNumber: initiatingUserMobilePhoneNumber }}
              components={{
                a: (
                  <TextActionLink
                    className="tw-whitespace-nowrap"
                    value={initiatingUserMobilePhoneNumber ?? ""}
                    htmlTag="span"
                    onClick={() =>
                      window.open(
                        `tel:${initiatingUserMobilePhoneNumber}`,
                        "_self",
                      )
                    }
                  />
                ),
              }}
            >
              {t("remoteIdentification.fraudClientErrorPage.description")}
            </Trans>
          }
        />
      );
    } else {
      return (
        <ErrorBlock
          title={t("remoteIdentification.genericErrorPage.title")}
          description={
            <Trans
              t={t}
              values={{ mobilePhoneNumber: initiatingUserMobilePhoneNumber }}
              components={{
                a: (
                  <TextActionLink
                    className="tw-whitespace-nowrap"
                    value={initiatingUserMobilePhoneNumber ?? ""}
                    htmlTag="span"
                    onClick={() =>
                      window.open(
                        `tel:${initiatingUserMobilePhoneNumber}`,
                        "_self",
                      )
                    }
                  />
                ),
              }}
            >
              {t("remoteIdentification.genericErrorPage.description")}
            </Trans>
          }
        />
      );
    }
  } else if (isLoading) {
    return <Preloader size="xl" className="tw-w-full tw-text-primary" />;
  }

  return (
    <LayoutOneColumn>
      {isFinnishBankIDOpen ? (
        <FIBankIDFlow
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment, @typescript-eslint/prefer-ts-expect-error
          // @ts-ignore - TODO: Fix this after converting the FI BankID flow to TypeScript
          identificationContext={identificationContext}
          onSuccess={onSuccess}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment, @typescript-eslint/prefer-ts-expect-error
          // @ts-ignore - TODO: Fix this after converting the FI BankID flow to TypeScript
          onFailure={refetch}
          onCancel={() => navigate(0)}
        />
      ) : (
        <>
          <h5 className="tw-pb-3">{title}</h5>

          {isToastOpen && (
            <Toast
              innerText={{
                subtitle: `${t(
                  "remoteIdentification.toastText",
                )} ${organisationName}`,
              }}
              onClick={() => setIsToastOpen(false)}
              className="tw-mb-5"
            />
          )}

          <div className="tw-rounded tw-border tw-border-primary tw-p-5">
            {contentData.map(({ label, value }, idx) => {
              if (value) {
                return (
                  <div key={idx} className="tw-flex tw-flex-row">
                    <Text size="md" className="tw-mr-2 tw-text-gray-700">
                      {label}
                    </Text>
                    <Text size="md">{value}</Text>
                  </div>
                );
              }

              return null;
            })}

            {isSweden ? (
              <div className="tw-mt-4 tw-border-t tw-pt-4">
                <BankIDFlow
                  hideHeading
                  identificationContext={identificationContext}
                  onSuccess={() => {
                    onSuccess();
                  }}
                  onFailure={async () => {
                    await refetch();
                  }}
                  authenticationContext={APPLICATION_NAME.CUSTOMER_WEB}
                />
              </div>
            ) : (
              <Button
                block
                size="lg"
                justify="center"
                id="open-finnish-bankid"
                className="tw-mx-auto tw-mt-5 tw-text-center"
                variant="contained"
                disabled={isFinnishBankIDOpen}
                onClick={() => setIsFinnishBankIDOpen(true)}
              >
                {t("login.startBankId")}
              </Button>
            )}

            <Text
              size="sm"
              className="tw-px-6 tw-pr-1 tw-pt-5 tw-text-center tw-font-bold"
            >
              <Trans i18nKey="applicationForm.commons.tosInfo">
                Placeholder: Genom att identifiera dig godkänner du&nbsp;
                <a
                  target="_blank"
                  href={termsOfServiceLink(
                    settings.countryCode,
                    settings.countryWebsiteUrl,
                    i18n.language,
                  )}
                  rel="noopener noreferrer"
                >
                  Placeholder: användarvillkoren
                </a>
              </Trans>
            </Text>
          </div>
        </>
      )}
    </LayoutOneColumn>
  );
};
